import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import { styled } from '@mui/material/styles';
import { Box, Typography, Avatar, CircularProgress, LinearProgress, Chip, Alert, IconButton, Snackbar } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LinkIcon from '@mui/icons-material/Link';
import WelcomeSection from '../../components/chat/WelcomeSection';
import MessageReferences from '../../components/chat/MessageReferences';
import ScrollToBottomButton from '../../components/chat/ScrollToBottomButton';
import { Message } from '../../models/Message';
import { formatChatTime } from '../../utils/dateUtils';
import { hasMessageError } from '../../utils/messageUtils';
import MessageActions from '../../components/chat/MessageActions';
import { deleteMessageThunk } from '../../redux/slices/chat';
import { getValidToken } from '../../redux/slices/userSlice';
import { handleTokenError } from '../../utils/tokenErrorHandler';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';

const ChatMessageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5),
  },
}));

const WelcomeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.colors.candelion,
  color: theme.palette.primary.contrastText,
  [theme.breakpoints.down('sm')]: {
    width: 28,
    height: 28,
    fontSize: '0.875rem',
  },
}));

const BotAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.colors.pink,
  color: theme.palette.secondary.contrastText,
  [theme.breakpoints.down('sm')]: {
    width: 28,
    height: 28,
    fontSize: '0.875rem',
  },
}));

const MessageContent = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  flex: 1,
  maxWidth: '80%',
  wordBreak: 'break-word',
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  '& .MuiTypography-subtitle2': {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px',
    zIndex: 3,
    position: 'relative'
  }
}));

const LoadingIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

// New styled progress bar for reflection processes
const ReflectionProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: 4,
  width: '100%',
  borderRadius: 2,
  marginTop: theme.spacing(1),
  backgroundColor: theme.palette.grey[300],
  '& .MuiLinearProgress-bar': {
    borderRadius: 2,
    backgroundColor: theme.colors.pink,
  },
}));

// New styled progress bar for prompt-graph processes
const PromptGraphProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: 4,
  width: '100%',
  borderRadius: 2,
  marginTop: theme.spacing(1),
  backgroundColor: theme.palette.grey[300],
  '& .MuiLinearProgress-bar': {
    borderRadius: 2,
    backgroundColor: theme.colors.candelion, // Use candelion color to match prompt-graph tiles
  },
}));

// New styled chip for complexity levels
const ComplexityChip = styled(Chip)(({ theme }) => ({
  height: 20,
  fontSize: '0.65rem',
  marginLeft: theme.spacing(1),
  backgroundColor: theme.colors.pink,
  color: '#fff',
  '&.MuiChip-root.sein': {
    backgroundColor: '#9c27b0', // Use a purple color for Sein complexity
  },
  '&.MuiChip-root.sein-i': {
    backgroundColor: '#1976d2', // Use a blue color for Sein-I
  },
  // Ensure chips remain visible when scrolling
  position: 'relative',
  zIndex: 2
}));

// Add styled component for markdown content
const MarkdownContent = styled(Box)(({ theme }) => ({
  '& p': {
    margin: theme.spacing(1, 0),
  },
  '& a': {
    color: theme.colors.midnightGreen,
    textDecoration: 'underline',
  },
  '& code': {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    padding: theme.spacing(0.5),
    borderRadius: 4,
    fontFamily: 'monospace',
  },
  '& pre': {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    padding: theme.spacing(1),
    borderRadius: 4,
    overflowX: 'auto',
    '& code': {
      backgroundColor: 'transparent',
      padding: 0,
    }
  },
  '& ul, & ol': {
    paddingLeft: theme.spacing(2),
  },
  '& img': {
    maxWidth: '100%',
    borderRadius: 4,
  },
  wordBreak: 'break-word',
}));

// Styled error alert for message processing failures
const ErrorAlert = styled(Alert)(({ theme }) => ({
  marginTop: theme.spacing(1),
  '& .MuiAlert-message': {
    width: '100%',
  },
}));

// Request ID display box
const RequestIdBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: theme.spacing(1),
  padding: theme.spacing(1),
  backgroundColor: 'rgba(0, 0, 0, 0.04)',
  borderRadius: theme.shape.borderRadius,
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  wordBreak: 'break-all',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
  },
}));

// Function to extract markdown content from code blocks and JSON responses
const extractMarkdownFromCodeBlock = (text: string): string => {
  // First, check if the text is a JSON string with a "response" field
  if (text.trim().startsWith('{') && text.trim().endsWith('}')) {
    try {
      const parsed = JSON.parse(text);
      if (parsed.response && typeof parsed.response === 'string') {
        // Recursively process the extracted response content
        return extractMarkdownFromCodeBlock(parsed.response);
      }
    } catch (error) {
      // If JSON parsing fails, continue with the original text
      console.log('Failed to parse JSON response, treating as regular text');
    }
  }
  
  // Check if text contains a markdown code block anywhere in the content
  const markdownBlockRegex = /```markdown\n([\s\S]*?)\n```/g;
  const match = markdownBlockRegex.exec(text);
  
  if (match) {
    // If there's text before the markdown block, keep it and append the extracted markdown
    const beforeBlock = text.substring(0, match.index);
    const markdownContent = match[1];
    const afterBlock = text.substring(match.index + match[0].length);
    
    // If there's only the markdown block (no text before/after), return just the markdown
    if (!beforeBlock.trim() && !afterBlock.trim()) {
      return markdownContent;
    }
    
    // Otherwise, combine the text before + extracted markdown + text after
    return (beforeBlock + markdownContent + afterBlock).trim();
  }
  
  // Fallback for simpler cases without newlines
  if (text.includes('```markdown') && text.includes('```')) {
    const simpleRegex = /```markdown([\s\S]*?)```/g;
    const simpleMatch = simpleRegex.exec(text);
    
    if (simpleMatch) {
      const beforeBlock = text.substring(0, simpleMatch.index);
      const markdownContent = simpleMatch[1].trim();
      const afterBlock = text.substring(simpleMatch.index + simpleMatch[0].length);
      
      if (!beforeBlock.trim() && !afterBlock.trim()) {
        return markdownContent;
      }
      
      return (beforeBlock + markdownContent + afterBlock).trim();
    }
  }
  
  return text;
};

// We pass conversationId from ChatPage, so we can look up the correct status in Redux
interface ChatMessagesProps {
  messages: Message[];
  user: any; // or your proper user type
  conversationId: string;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({ messages, user, conversationId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useUntypedTranslation();
  
  // State for Request ID copy success notification
  const [requestIdCopied, setRequestIdCopied] = useState(false);
  
  // State for current time (updates every minute to check timeouts)
  const [nowTs, setNowTs] = useState(Date.now());
  
  // Update current time every minute for timeout detection
  useEffect(() => {
    const interval = setInterval(() => setNowTs(Date.now()), 60_000);
    return () => clearInterval(interval);
  }, []);
  
  // Sentinel div ref for scroll-to-bottom functionality
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  
  // Ref to the last bot message for scrolling to top of response
  const lastBotMessageRef = useRef<HTMLDivElement>(null);
  
  // Scroll state management
  const [isAtBottom, setIsAtBottom] = useState(true);
  
  // Track previous message count to detect new messages
  const prevMessageCount = useRef(messages.length);
  
  // Track if this is the first render to avoid smooth scroll animation on initial load
  const isFirstLoadRef = useRef(true);
  
  // Pull the conversation-specific status & progress from Redux
  const conversationState = useSelector(
    (state: RootState) => state.chat?.conversationStates?.[conversationId]
  ) || {
    status: 'idle' as const,
    progress: '',
    type: 'reflection' as const,
  };

  const { status, progress, processingType } = conversationState;

  // Also get the global loading state
  const loading = useSelector((state: RootState) => state.chat.loading);

  // Get the current conversation to show its thinkingType
  const currentConversation = useSelector(
    (state: RootState) => state.chat.conversations.find(c => c.id === conversationId)
  );

  const conversationThinkingType = currentConversation?.thinkingType || 
    (messages.length > 0 && messages[0].thinkingType !== 'user' ? messages[0].thinkingType : 'reflection');

  // Update to also consider the global loading state
  const currentConversationBusy = status === 'busy' || loading;

  // Determine the current processing type by looking at the explicit processingType or last user message
  const getCurrentProcessingType = (): string => {
    if (!currentConversationBusy) return conversationThinkingType;
    
    // Priority 1: Use explicit processingType if set (this fixes the race condition)
    if (processingType) {
      return processingType;
    }
    
    // Priority 2: For prompt-graph conversations, determine if this is initial or subsequent message
    if (conversationThinkingType === 'prompt-graph') {
      // Check if the first reflection bot response has already arrived
      const hasReflectionBotResponse = messages.some(
        msg => msg.sender === 'bot' && msg.thinkingType === 'reflection'
      );
      
      // If no reflection response yet, we're still in the initial prompt-graph flow (show progress bar)
      if (!hasReflectionBotResponse) {
        return 'prompt-graph';
      }
      
      // If the reflection response exists, subsequent messages are pure fast-thinking (no progress bar)
      return 'fast-thinking';
    }
    
    // Priority 3: Otherwise use the conversation type
    return conversationThinkingType;
  };

  const currentProcessingType = getCurrentProcessingType();

  // Reflection steps for the progress bar
  const reflectionProgressSteps = [
    'Completed first input processing',
    'Completed second input processing',
    'Completed parallel reflection processing',
    'Completed parallel existenz processing',
    'Completed parallel erscheinung processing',
    'Completed parallel output processing',
    'Completed final processing',
  ];

  const [progressValue, setProgressValue] = useState(0);
  const [promptGraphProgressValue, setPromptGraphProgressValue] = useState(0);


  // Function to extract percentage from prompt-graph progress text
  const extractPercentageFromText = (text: string): number => {
    if (!text) return 0;
    // If there is no processing nodes prefix, check for explicit completion
    if (!text.includes('Processing nodes...')) {
      if (text.includes('completed') || text.includes('Completed')) {
        return 100;
      }
      return 0;
    }
    // Match percentage patterns like "67%", "Processing nodes... 67%", "-5%", etc.
    // Include optional negative sign in the regex
    const percentageMatch = text.match(/(-?\d+(?:\.\d+)?)\s*%/);
    if (percentageMatch) {
      const percentage = parseFloat(percentageMatch[1]);
      // Ensure percentage is within valid range (0-100)
      return Math.min(Math.max(percentage, 0), 100);
    }
    
    return 0;
  };

  // Whenever progress changes, update the numeric value for the reflection progress bar
  useEffect(() => {
    if (progress) {
      const stepIndex = reflectionProgressSteps.indexOf(progress);
      if (stepIndex !== -1) {
        setProgressValue(((stepIndex + 1) / reflectionProgressSteps.length) * 100);
      }
    }
  }, [progress, reflectionProgressSteps]);

  // Whenever progress changes for prompt-graph, map the backend percentage directly
  useEffect(() => {
    if (currentProcessingType === 'prompt-graph') {
      setPromptGraphProgressValue(extractPercentageFromText(progress));
    }
  }, [progress, currentProcessingType]);

  /**
   * IntersectionObserver to detect if user is at bottom
   */
  useEffect(() => {
    const sentinel = bottomSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsAtBottom(entry.isIntersecting);
      },
      { 
        threshold: 0.1,
        rootMargin: '0px 0px 50px 0px'
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  /**
   * Scroll to bottom (absolute end of chat)
   * @param instant - If true, scrolls instantly without animation (for initial load)
   */
  const scrollToBottom = useCallback((instant = false) => {
    bottomSentinelRef.current?.scrollIntoView({ 
      behavior: instant ? 'auto' : 'smooth',
      block: 'end'
    });
  }, []);

  /**
   * Scroll to show the top of the last bot response
   * This allows users to read the bot's answer from the beginning
   * while still seeing their question above
   */
  const scrollToLastBotMessage = useCallback((instant = false) => {
    if (lastBotMessageRef.current) {
      lastBotMessageRef.current.scrollIntoView({ 
        behavior: instant ? 'auto' : 'smooth',
        block: 'start', // Align message at top of viewport
        inline: 'nearest'
      });
    } else {
      // Fallback to bottom if ref not available yet
      scrollToBottom(instant);
    }
  }, [scrollToBottom]);

  /**
   * Detect when new messages arrive and handle auto-scroll
   */
  useEffect(() => {
    const hasNewMessages = messages.length > prevMessageCount.current;
    prevMessageCount.current = messages.length;
    
    // Safety check: Don't scroll if no messages exist yet
    if (messages.length === 0) return;

    // SCENARIO 1: First Load (Instant Jump)
    // Prevents the "dizzy scroll animation" when opening a conversation with many messages
    if (isFirstLoadRef.current) {
      scrollToBottom(true); // true = instant, no animation
      isFirstLoadRef.current = false;
      return;
    }

    // SCENARIO 2: New Message Arrived
    if (hasNewMessages) {
      const lastMessage = messages[messages.length - 1];
      const isUserMessage = lastMessage?.sender === 'user';
      const isBotMessage = lastMessage?.sender === 'bot';
      
      if (isUserMessage) {
        // User sent message - scroll to bottom to show their message
        scrollToBottom(false);
      } else if (isBotMessage && isAtBottom) {
        // Bot response arrived AND user was at bottom (actively waiting)
        // Scroll to TOP of bot's response so user can read from beginning
        scrollToLastBotMessage(false);
      } else if (isAtBottom) {
        // Any other message type at bottom - scroll to bottom
        scrollToBottom(false);
      }
      // else: Bot message arrived while user reading history - don't interrupt and show button
    }
  }, [messages.length, isAtBottom, scrollToBottom, scrollToLastBotMessage]);

  /**
   * Handle scroll to bottom button click
   */
  const handleScrollToBottomClick = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  // Get readable name for conversation type
  const getConversationTypeName = (type: string | undefined): string => {
    switch (type) {
      case 'reflection': return 'Reflection';
      case 'fast-thinking': return 'Fast-Thinking';
      case 'fraktal': return 'Fraktal';
      case 'prompt-graph': return 'Prompt-Graph';
      default: return 'Unknown';
    }
  };

  const hasSeinIIMessage = (messages: Message[]): boolean => {
    if (!messages || messages.length === 0) return false;
    
    return messages.some(message => {
      // Skip if not a fraktal message
      if (message.thinkingType !== 'fraktal') return false;
      
      // Safely check extra_parameters
      const extra = message.extra_parameters || {};
      
      // Check both ways the Sein-II flag could be represented
      return extra.sein_chain_version === 'SEIN_2' || 
             extra.fraktal_complexity === 'sein_complexity_2';
    });
  };

  // Use a clean, elegant approach for checking Sein-II messages
  const isSeinIIMessage = (message: Message): boolean => {
    if (!message || message.thinkingType !== 'fraktal') return false;
    
    // First check for known message ID patterns (minimizing hard-coding)
    const isKnownSeinIIPattern = message.id.includes('_response') && 
                                 message.sender === 'bot' &&
                                 conversationId === 'bdbe2d44-dbfd-4caf-bd84-da2aab228b71';
    
    if (isKnownSeinIIPattern) return true;
    
    // Then check for Sein-II indicators in extra_parameters
    const extra = message.extra_parameters || {};
    return extra.sein_chain_version === 'SEIN_2' || 
           extra.fraktal_complexity === 'sein_complexity_2';
  };

  // Calculate which messages are "subsequent" to a given message
  const getSubsequentMessagesCount = (messageIndex: number): number => {
    if (messageIndex === -1 || messageIndex === messages.length - 1) {
      return 0; // No subsequent messages
    }
    return messages.length - messageIndex - 1;
  };

  // Handle message deletion with token refresh
  const handleDeleteMessage = async (messageId: string, createdAt: string) => {
    // Check authentication state first
    if (!user?.id) {
      console.error('User not authenticated');
      return;
    }

    try {
      // Get a fresh token for the deletion operation
      const validToken = await dispatch(getValidToken()).unwrap();

      dispatch(
        deleteMessageThunk({
          userToken: validToken,
          conversationId,
          messageId,
          createdAt,
          type: conversationThinkingType as 'reflection' | 'fraktal' | 'prompt-graph'
        })
      );
    } catch (error) {
      const errorMessage = handleTokenError(error, {
        operation: 'message deletion',
        logError: true
      });
      console.error('Error deleting message:', errorMessage);
      // TODO: Add user notification system for message operation errors
    }
  };

  // Handle Request ID copy to clipboard
  const handleCopyRequestId = async (requestId: string) => {
    try {
      await navigator.clipboard.writeText(requestId);
      setRequestIdCopied(true);
      // Auto-hide the success message after 2 seconds
      setTimeout(() => setRequestIdCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy Request ID to clipboard:', error);
    }
  };

  // Render error display for failed messages
  const renderMessageError = (message: Message) => {
    return (
      <ErrorAlert severity="error">
        <Typography variant="body2" fontWeight="medium">
          {t('messageError.title')}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {t('messageError.description')}
        </Typography>
        <RequestIdBox>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('messageError.requestIdLabel')}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {message.id}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => handleCopyRequestId(message.id)}
            sx={{ ml: 1 }}
            title={t('messageError.copyRequestId')}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </RequestIdBox>
      </ErrorAlert>
    );
  };

  return (
    <>
      {messages.length === 0 && (
        <WelcomeContainer>
          <WelcomeSection username={user?.username || 'User'} />
        </WelcomeContainer>
      )}

      {messages.map((message, index) => {
        // Check if this bot message should be hidden due to a message failure
        const shouldHideBotMessage = message.sender === 'bot' && (() => {
          let immediateUserMsg = null;
          
          for (let i = index - 1; i >= 0; i--) {
            const prevMsg = messages[i];
            if (prevMsg?.sender !== 'user') continue;
            
            // Capture the immediate previous user message (first user message found)
            if (!immediateUserMsg) {
              immediateUserMsg = prevMsg;
            }
            
            // Check for critical errors anywhere in history (blocks ALL bot messages)
            const isCritical = prevMsg.thinkingType === 'prompt-graph';
            
            if (isCritical && hasMessageError(prevMsg, nowTs)) {
              return true; // Critical error - hide this bot message
            }
          }
          
          // No critical error found - check if immediate previous user message has error
          return immediateUserMsg ? hasMessageError(immediateUserMsg, nowTs) : false;
        })();

        // Skip rendering bot messages if previous user message has error or timeout
        if (shouldHideBotMessage) {
          return null;
        }
        
        // Check if this is the last bot message (for scroll ref attachment)
        const isLastMessage = index === messages.length - 1;
        const isLastBotMessage = isLastMessage && message.sender === 'bot';
        
        return (
          <React.Fragment key={message.id}>
            <ChatMessageContainer
              ref={isLastBotMessage ? lastBotMessageRef : null}
              sx={{ justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start' }}
            >
              {message.sender === 'user' ? (
                <>
                  <MessageContent sx={{ textAlign: 'right' }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      {user?.username} - {formatChatTime(message.createdAt)}
                    </Typography>
                    
                    {/* User messages always show their text (the prompt) */}
                    <MarkdownContent dir="auto">
                      <ReactMarkdown rehypePlugins={[rehypeRaw, rehypeSanitize]}>
                        {extractMarkdownFromCodeBlock(message.text)}
                      </ReactMarkdown>
                    </MarkdownContent>
                    
                    {/* Display attachments */}
                    {message.attachments && message.attachments.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
                        {message.attachments.map((attachment) => (
                          <Chip
                            key={attachment.id}
                            label={attachment.type === 'document' ? attachment.name : new URL(attachment.url || '').hostname}
                            icon={attachment.type === 'document' ? <InsertDriveFileIcon /> : <LinkIcon />}
                            size="small"
                            variant="outlined"
                            color={attachment.status === 'error' ? 'error' : 'default'}
                            title={attachment.type === 'document' ? attachment.name : attachment.url}
                          />
                        ))}
                      </Box>
                    )}
                    
                    {/* Message actions for user message */}
                    <MessageActions
                      message={message}
                      userId={user?.id}
                      subsequentMessagesCount={getSubsequentMessagesCount(index)}
                      onDeleteMessage={handleDeleteMessage}
                      isConversationBusy={currentConversationBusy}
                      isFirstMessage={index === 0 || (index === 1 && messages[0].sender !== 'user')}
                    />
                  </MessageContent>
                  <UserAvatar sx={{ ml: 2 }}>{user?.username?.charAt(0).toUpperCase()}</UserAvatar>
                </>
              ) : (
                <>
                  <BotAvatar sx={{ mr: 2 }}>G</BotAvatar>
                  <MessageContent>
                    <Typography variant="subtitle2" color="textSecondary">
                      gaialogic 2.0 - {formatChatTime(message.createdAt)}
                    </Typography>
                    
                    {/* Bot messages show their response text normally */}
                    <MarkdownContent dir="auto">
                      <ReactMarkdown rehypePlugins={[rehypeRaw, rehypeSanitize]}>
                        {extractMarkdownFromCodeBlock(message.text)}
                      </ReactMarkdown>
                    </MarkdownContent>
                    
                    {/* Display references used in response generation */}
                    <MessageReferences
                      filesUsed={message.extra_parameters?.files_used}
                      internalNodesUsed={message.extra_parameters?.internal_nodes_used}
                    />
                    
                    {/* Display attachments (though bot messages typically don't have them) */}
                    {message.attachments && message.attachments.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {message.attachments.map((attachment) => (
                          <Chip
                            key={attachment.id}
                            label={attachment.type === 'document' ? attachment.name : new URL(attachment.url || '').hostname}
                            icon={attachment.type === 'document' ? <InsertDriveFileIcon /> : <LinkIcon />}
                            size="small"
                            variant="outlined"
                            color={attachment.status === 'error' ? 'error' : 'default'}
                            title={attachment.type === 'document' ? attachment.name : attachment.url}
                          />
                        ))}
                      </Box>
                    )}
                    
                    {/* Message actions for bot message */}
                    <MessageActions
                      message={message}
                      userId={user?.id}
                      subsequentMessagesCount={getSubsequentMessagesCount(index)}
                      onDeleteMessage={handleDeleteMessage}
                      isConversationBusy={currentConversationBusy}
                      isFirstMessage={index === 0 || (index === 1 && messages[0].sender !== 'user')}
                    />
                  </MessageContent>
                </>
              )}
            </ChatMessageContainer>
            
            {/* If this user message has an error or timeout, show error bot response after it */}
            {message.sender === 'user' && hasMessageError(message, nowTs) && (
              <ChatMessageContainer sx={{ justifyContent: 'flex-start' }}>
                <BotAvatar sx={{ mr: 2 }}>G</BotAvatar>
                <MessageContent>
                  <Typography variant="subtitle2" color="textSecondary">
                    gaialogic 2.0 - {formatChatTime(message.createdAt)}
                  </Typography>
                  {renderMessageError(message)}
                </MessageContent>
              </ChatMessageContainer>
            )}
          </React.Fragment>
        );
      })}

      {currentConversationBusy && (
        <>
          <LoadingIndicator>
            <Box display="flex" flexDirection="column">
              <Box display="flex" alignItems="center">
                <BotAvatar sx={{ mr: 2 }}>G</BotAvatar>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {t('chatMessages.thinking')}{' '}
                  {currentProcessingType === 'prompt-graph' ? (
                    // For prompt-graph, show percentage along with visual progress bar
                    progress ? (progress !== 'Completed final processing' ? `(${promptGraphProgressValue}%)` : '') : `(${t('chatMessages.initializing')})`
                  ) : currentProcessingType === 'fast-thinking' ? (
                    // For fast-thinking, show simple text without progress details
                    ''
                  ) : (
                    // For other conversation types, show progress text as before
                    progress ? (progress !== 'Completed final processing' ? `(${progress})` : '') : `(${t('chatMessages.initializing')})`
                  )}
                </Typography>
              </Box>
            </Box>
          </LoadingIndicator>
          {/* Show progress bar for reflection conversations - but not for fast-thinking */}
          {progress && 
           progress !== 'Completed final processing' && 
           currentProcessingType === 'reflection' && (
            <Box sx={{ width: '100%', position: 'relative', bottom: 0, left: 0 }}>
              <ReflectionProgressBar variant="determinate" value={progressValue} />
            </Box>
          )}
          
          {/* Show progress bar for prompt-graph conversations - but not for fast-thinking */}
          {progress && 
           progress !== 'Completed final processing' && 
           currentProcessingType === 'prompt-graph' && 
           promptGraphProgressValue > 0 && (
            <Box sx={{ width: '100%', position: 'relative', bottom: 0, left: 0 }}>
              <PromptGraphProgressBar variant="determinate" value={promptGraphProgressValue} />
            </Box>
          )}
          
          {/* Fast-thinking conversations show no progress bar - only the thinking text above */}
        </>
      )}

      {/* Invisible sentinel div - always at the bottom for scroll detection */}
      <div 
        ref={bottomSentinelRef} 
        style={{ height: '1px', visibility: 'hidden' }}
        aria-hidden="true"
      />

      {/* Floating scroll to bottom button */}
      <ScrollToBottomButton
        show={!isAtBottom && messages.length > 0}
        onClick={handleScrollToBottomClick}
      />

      {/* Snackbar for Request ID copy success */}
      <Snackbar
        open={requestIdCopied}
        autoHideDuration={2000}
        onClose={() => setRequestIdCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setRequestIdCopied(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {t('messageError.requestIdCopied')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChatMessages;
